/**
 * The answer panel: a 2×2 grid of big multiple-choice buttons (keys 1–4 /
 * A–D) or the handwriting pad, plus a small toggle to switch between them.
 *
 * Both submit plain text through the same engine call: a choice is sent as
 * its English-formatted value (choices.ts), so it goes through parseAnswer
 * exactly like typed or handwritten answers. After an answer the grid shows,
 * kindly, which button was right.
 */
import { memo, useEffect, useId, useMemo, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { eq, type InputVia, type Locale, type Problem, type Resolution } from '@dropstack/core';
import type { HandwritingRecognizer, HwResult } from '../contracts.ts';
import { Icon } from '../components/Icon.tsx';
import { useT } from '../i18n/index.ts';
import { HandwritingPad } from '../input/HandwritingPad.tsx';
import { CHOICE_INDICES, CHOICE_LETTERS, choiceIndexForKey, choiceKey, choiceSubmitValue, type ChoiceIndex } from './choices.ts';
import { GameIcon } from './icons.tsx';
import { INPUT_MODES, modeForRadioKey, type InputMode } from './inputMode.ts';
import { MathNodes } from './MathText.tsx';
import { speechFor, valueMarkup, valueView } from './mathView.ts';

/** Key events aimed at a text field, a dialog or a modified shortcut are not answers. */
export function isForeignKey(e: KeyboardEvent): boolean {
  if (e.defaultPrevented || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return true;
  const el = e.target instanceof Element ? e.target : null;
  if (!el) return false;
  if (el.closest('dialog')) return true;
  return el instanceof HTMLElement && (el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT');
}

type ChoiceFeedback = 'idle' | 'right' | 'picked' | 'other';

function feedbackFor(problem: Problem, resolution: Resolution | null, i: ChoiceIndex): ChoiceFeedback {
  if (!resolution || resolution.problem.id !== problem.id) return 'idle';
  if (i === problem.correctChoice) return 'right';
  const given = resolution.given;
  return resolution.kind === 'wrong' && given !== null && eq(given, problem.choices[i]) ? 'picked' : 'other';
}

interface ChoiceGridProps {
  problem: Problem;
  resolution: Resolution | null;
  disabled: boolean;
  locale: Locale;
  compact: boolean;
  onPick(index: ChoiceIndex): void;
}

const ChoiceGrid = memo(function ChoiceGrid({ problem, resolution, disabled, locale, compact, onPick }: ChoiceGridProps) {
  const t = useT();
  const hintId = useId();
  const views = useMemo(
    () =>
      problem.choices.map((c) => ({
        nodes: valueView(c, problem.answerKind, locale),
        speech: speechFor(valueMarkup(c, problem.answerKind), locale),
      })),
    [problem, locale],
  );

  // Keyboard: 1–4 or A–D.
  useEffect(() => {
    if (disabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (isForeignKey(e)) return;
      const i = choiceIndexForKey(e.key);
      if (i === null) return;
      e.preventDefault();
      onPick(i);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled, onPick]);

  return (
    <div className={`choices${compact ? ' choices--compact' : ''}`} role="group" aria-label={t('game.choicesLabel')} aria-describedby={hintId}>
      {CHOICE_INDICES.map((i) => {
        const view = views[i]!;
        const letter = CHOICE_LETTERS[i];
        return (
          <button
            // Stable across problems (choices.ts): the same four buttons, and the focus on one of them, carry on to the next sum.
            key={choiceKey(i)}
            type="button"
            className={`choice choice--${feedbackFor(problem, resolution, i)}`}
            // aria-disabled (not disabled) so a focused button keeps focus between problems.
            aria-disabled={disabled || undefined}
            aria-label={t('game.choiceLabel', { letter, value: view.speech })}
            aria-keyshortcuts={`${i + 1} ${letter}`}
            onClick={() => {
              if (!disabled) onPick(i);
            }}
          >
            {!compact && (
              <span className="choice__letter" aria-hidden="true">
                {letter}
              </span>
            )}
            <MathNodes nodes={view.nodes} className="choice__value" />
            {feedbackFor(problem, resolution, i) === 'right' && <Icon name="check" className="choice__tick" />}
          </button>
        );
      })}
      <p id={hintId} className="visually-hidden">
        {t('game.keysHint')}
      </p>
    </div>
  );
});

interface ModeToggleProps {
  mode: InputMode;
  onChoose(mode: InputMode): void;
}

/** Choices ⇄ Write, for the rest of the run. */
function ModeToggle({ mode, onChoose }: ModeToggleProps) {
  const t = useT();
  const buttons = useRef<Partial<Record<InputMode, HTMLButtonElement | null>>>({});

  // One Tab stop (the checked option); the arrow keys switch, as in any radio group.
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const next = modeForRadioKey(e.key, mode);
    if (next === null) return;
    e.preventDefault();
    if (next !== mode) onChoose(next);
    buttons.current[next]?.focus();
  };

  const options: Readonly<Record<InputMode, { label: string; icon: ReactNode }>> = {
    mc: { label: t('game.modeChoices'), icon: <GameIcon name="choices" /> },
    hw: { label: t('game.modeWrite'), icon: <Icon name="pencil" /> },
  };

  return (
    <div className="mode-toggle" role="radiogroup" aria-label={t('game.modeLabel')} onKeyDown={onKeyDown}>
      {INPUT_MODES.map((m) => (
        <button
          key={m}
          ref={(el) => {
            buttons.current[m] = el;
          }}
          type="button"
          role="radio"
          aria-checked={mode === m}
          tabIndex={mode === m ? 0 : -1}
          className="mode-toggle__option"
          title={options[m].label}
          onClick={() => onChoose(m)}
        >
          {options[m].icon}
          <span className="mode-toggle__text">{options[m].label}</span>
        </button>
      ))}
    </div>
  );
}

export interface AnswerPanelProps {
  /** The problem being answered — or the one just resolved, shown with feedback until the next spawns. */
  problem: Problem | null;
  resolution: Resolution | null;
  mode: InputMode;
  /** Inputs work only while a block is falling (and the game isn't paused). */
  disabled: boolean;
  locale: Locale;
  recognizer: HandwritingRecognizer;
  compact: boolean;
  onAnswer(value: string, via: InputVia): void;
  /** The pad gave up on this problem (or the player tapped "Choices"). */
  onFallback(): void;
  onChooseMode(mode: InputMode): void;
  onRecognized(result: HwResult): void;
}

export const AnswerPanel = memo(function AnswerPanel(props: AnswerPanelProps) {
  const { problem, resolution, mode, disabled, locale, recognizer, compact, onAnswer, onFallback, onChooseMode, onRecognized } = props;

  const onPick = useMemo(
    () => (i: ChoiceIndex) => {
      if (problem) onAnswer(choiceSubmitValue(problem.choices[i], problem.answerKind), 'mc');
    },
    [problem, onAnswer],
  );

  return (
    <section className={`answers answers--${mode}${compact ? ' answers--compact' : ''}`}>
      {!compact && <ModeToggle mode={mode} onChoose={onChooseMode} />}
      {mode === 'hw' ? (
        <HandwritingPad
          recognizer={recognizer}
          resetKey={problem?.id ?? 'none'}
          disabled={disabled || !problem}
          compact={compact}
          onSubmit={onAnswer}
          onFallbackToChoices={onFallback}
          onRecognized={onRecognized}
        />
      ) : problem ? (
        <ChoiceGrid problem={problem} resolution={resolution} disabled={disabled} locale={locale} compact={compact} onPick={onPick} />
      ) : (
        <div className="choices choices--empty" aria-hidden="true" />
      )}
    </section>
  );
});
